// Top-level layout: sidebar + main outlet. Focused task routes own their full
// screen chrome and contextual exit actions; everything else gets the sidebar.
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import CompanySwitcher from '../components/CompanySwitcher.jsx';
import Sidebar from '../components/Sidebar.jsx';
import SiteFooter from '../components/SiteFooter.jsx';
import { BillingNotice } from '../components/CompanyBilling.jsx';
import { PALETTE } from '../palette.js';
import { logout } from '../api.js';
import { hasNamedTenantAction } from '../product-policy.js';
import { resetTenantScrollOwner, tenantRouteScrollKey } from './tenant-scroll.js';

const FULLBLEED = new Set(['wizard', 'building', 'workflow', 'accept-invite']);
const useBrowserLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

function useCompactShell() {
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 760px)');
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return compact;
}

export default function AppShell({ route, navigate, session, children }) {
  const focusedBilling = route.name === 'billing' && ['checkout', 'manage'].includes(route.rest?.[0]);
  const showSidebar = !FULLBLEED.has(route.name) && !focusedBilling;
  const compact = useCompactShell();
  const mainRef = useRef(null);
  const [switching, setSwitching] = useState(false);
  const routeScrollKey = tenantRouteScrollKey(route);
  const businessName =
    session.user?.activeCompany?.name ||
    session.user?.activeBrandName ||
    session.user?.email ||
    'Not set up yet';
  const userName =
    session.user?.displayName ||
    session.user?.email?.split('@')[0] ||
    'Account';
  const hasTenantAccess = Boolean(session.user?.activeCompany?.id || session.user?.memberships?.length);
  const mvpMode = session.user?.auth?.type === 'supabase' && Boolean(session.user?.productPolicy);
  const showSiteFooter = session.user?.auth?.type === 'supabase';
  const legacyCreationRetired = session.user?.auth?.type === 'legacy';
  const tenantShellActive = showSidebar && mvpMode;
  const saasScrollOwnerActive = showSidebar && showSiteFooter;
  const canReadLeads = mvpMode
    ? hasNamedTenantAction(session.user.productPolicy, 'leads.read')
    : ['owner', 'admin', 'marketer'].includes(String(session.user?.role || '').toLowerCase());
  const canCreateCampaign = mvpMode
    ? hasNamedTenantAction(session.user.productPolicy, 'campaign.draft.create')
    : false;
  const shellClassName = [
    tenantShellActive ? 'bl-tenant-shell' : '',
    showSiteFooter ? 'bl-brand-surface' : '',
  ].filter(Boolean).join(' ') || undefined;

  useBrowserLayoutEffect(() => {
    if (!tenantShellActive) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const rootAlreadyLocked = root.classList.contains('bl-tenant-shell-active');
    const bodyAlreadyLocked = body.classList.contains('bl-tenant-shell-active');
    root.classList.add('bl-tenant-shell-active');
    body.classList.add('bl-tenant-shell-active');
    resetTenantScrollOwner(mainRef.current);
    return () => {
      if (!rootAlreadyLocked) root.classList.remove('bl-tenant-shell-active');
      if (!bodyAlreadyLocked) body.classList.remove('bl-tenant-shell-active');
    };
  }, [tenantShellActive]);

  useBrowserLayoutEffect(() => {
    if (!tenantShellActive) return;
    resetTenantScrollOwner(mainRef.current);
  }, [routeScrollKey, tenantShellActive]);

  if (!showSidebar) {
    return (
      <div className={showSiteFooter ? 'bl-brand-surface' : undefined} style={{ minHeight: '100vh', background: PALETTE.bg }}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={shellClassName}
      data-compact={tenantShellActive ? (compact ? 'true' : 'false') : undefined}
      style={tenantShellActive ? { background: PALETTE.bg } : {
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        height: '100vh',
        minWidth: 0,
        background: PALETTE.bg,
        overflow: 'hidden',
      }}
    >
      <Sidebar
        compact={compact}
        current={route.name}
        onNav={(s) => {
          if (switching) return;
          if (s === 'home') navigate('home');
          else navigate(s);
        }}
        businessName={businessName}
        userName={userName}
        isPlatformAdmin={Boolean(session.user?.platform?.admin)}
        hasTenantAccess={hasTenantAccess}
        canReadLeads={canReadLeads}
        canCreateCampaign={canCreateCampaign}
        mvpMode={mvpMode}
        legacyCreationRetired={legacyCreationRetired}
        platformCapabilities={session.user?.platform?.capabilities || []}
        platformView={route.name === 'platform' ? route.rest?.[0] || 'overview' : ''}
        productPolicy={session.user?.productPolicy || null}
        companySwitcher={mvpMode ? <CompanySwitcher session={session} navigate={navigate} route={route} mainRef={mainRef} onSwitching={setSwitching} /> : null}
        onLogout={logout}
      />
      <main
        ref={mainRef}
        inert={switching ? "" : undefined}
        className={tenantShellActive ? 'bl-tenant-scroll-owner' : undefined}
        data-tenant-scroll-owner={saasScrollOwnerActive ? 'true' : undefined}
        style={tenantShellActive ? undefined : {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: showSiteFooter ? 'flex' : undefined,
          flexDirection: showSiteFooter ? 'column' : undefined,
          overflowY: 'auto',
          overflowX: 'hidden',
          height: compact ? 'auto' : '100vh',
        }}
      >
        {mvpMode && !['billing', 'platform'].includes(route.name) && <BillingNotice navigate={navigate} />}
        <Fragment key={session.user?.activeCompany?.id || "no-company"}>{children}</Fragment>
        {showSiteFooter && <SiteFooter />}
      </main>
    </div>
  );
}
